/**
 * Admin API Client
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const TOKEN_KEY = 'admin_token';

// Get stored token
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// Common fetch with auth
const fetchWithAuth = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'X-Admin-Token': token }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (response.status === 401) {
    clearToken();
    window.location.href = '/admin';
    throw new Error('Unauthorized');
  }

  return response;
};

// Auth
export const login = async (password) => {
  const response = await fetch(`${API_BASE}/api/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
    credentials: 'include',
  });

  if (response.ok) {
    setToken(password);
    return true;
  }
  return false;
};

export const logout = async () => {
  await fetchWithAuth('/api/admin/logout', { method: 'POST' });
  clearToken();
};

export const verify = async () => {
  try {
    const response = await fetchWithAuth('/api/admin/verify');
    return response.ok;
  } catch {
    return false;
  }
};

// Stats
export const getStats = async () => {
  const response = await fetchWithAuth('/api/admin/stats');
  if (!response.ok) throw new Error('Failed to fetch stats');
  return response.json();
};

// Profiles
export const getProfiles = async (page = 1, perPage = 20, search = '') => {
  const params = new URLSearchParams({ page, per_page: perPage, search });
  const response = await fetchWithAuth(`/api/admin/profiles?${params}`);
  if (!response.ok) throw new Error('Failed to fetch profiles');
  return response.json();
};

export const getProfile = async (id) => {
  const response = await fetchWithAuth(`/api/admin/profiles/${id}`);
  if (!response.ok) throw new Error('Failed to fetch profile');
  return response.json();
};

export const deleteProfile = async (id) => {
  const response = await fetchWithAuth(`/api/admin/profiles/${id}`, { method: 'DELETE' });
  if (!response.ok) throw new Error('Failed to delete profile');
  return response.json();
};

// Messages
export const getMessages = async (page = 1, perPage = 20, filter = 'all') => {
  const params = new URLSearchParams({ page, per_page: perPage, filter });
  const response = await fetchWithAuth(`/api/admin/messages?${params}`);
  if (!response.ok) throw new Error('Failed to fetch messages');
  return response.json();
};

export const approveMessage = async (id, approved) => {
  const response = await fetchWithAuth(`/api/admin/messages/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ approved }),
  });
  if (!response.ok) throw new Error('Failed to approve message');
  return response.json();
};

export const deleteMessage = async (id) => {
  const response = await fetchWithAuth(`/api/admin/messages/${id}`, { method: 'DELETE' });
  if (!response.ok) throw new Error('Failed to delete message');
  return response.json();
};

// Settings
export const getSettings = async () => {
  const response = await fetchWithAuth('/api/admin/settings');
  if (!response.ok) throw new Error('Failed to fetch settings');
  return response.json();
};

// Jobs
export const getJobs = async () => {
  const response = await fetchWithAuth('/api/admin/jobs');
  if (!response.ok) throw new Error('Failed to fetch jobs');
  return response.json();
};
